import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { createGlobalStyle } from 'styled-components';
import postal from 'postal';
import toastr from 'toastr';
import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import Pagination from '../../../shared/pagination';
import Alert from '../../../shared/alert';
import ErrorBoundary from '../../../shared/error-boundary';
import { getLocationState } from '../../../shared/location';
import { getPaginated, recalcAllWires, getWireStats } from '../../../services/lwadmin/trending/trending-service';

const keys = [
  'number_of_wire_impressions', 'number_of_feed_impressions', 'number_of_likes',
  'number_of_comments', 'number_of_shares', 'number_of_profile_link_clicks',
  'number_of_external_link_clicks', 'number_of_pdf_link_clicks',
];
const pfxLen = 'number_of_'.length;

const WireStatTable = props => (
  <table className="table">
    <tbody>
      {keys.map(key => (
        <tr key={key}>
          <th>{_.capitalize(_.lowerCase(key.slice(pfxLen)))}</th>
          <td>{props[key]}</td>
        </tr>
      ))}
      <tr>
        <th>Age</th>
        <td>{Math.round(props.age_of_wire_in_days * 100) / 100}</td>
      </tr>
    </tbody>
  </table>
);

WireStatTable.propTypes = {
  ...keys.reduce((r, key) => _.set(r, key, PropTypes.number.isRequired), {}),
  age_of_wire_in_days: PropTypes.number.isRequired,
};

const Hint = ({ text }) => (
  <OverlayTrigger
    placement="right-end"
    overlay={<Popover id={`pop-${text.slice(0, 5)}`} bsPrefix="trending-hint">{text}</Popover>}
  >
    <a href="#">?</a>
  </OverlayTrigger>
);

Hint.propTypes = { text: PropTypes.string.isRequired };

const GlobalStyle = createGlobalStyle`
  .trending-hint {
    color: white;
    font: 700 14px/120% "Texta", Arial, Helvetica, sans-serif;
    width: 200px;
    border-radius: 6px;
    background-color: rgb(33, 37, 41);
    padding: 8px 20px;
    box-shadow: 0 0 9px 5px rgba(53, 65, 76, 0.2);
  }
`;

export default class TrendingTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.triggerTrendingRecalcForAllWires = this.triggerTrendingRecalcForAllWires.bind(this);
    this.refresh = this.refresh.bind(this);

    this.state = {
      wires: [],
      totalWireCount: 0,
      page: 1,
      totalPageCount: 1,
    };
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'lwadmin.trending.commands',
      topic: 'trigger-trending-recalc-for-all-wires',
      callback: () => this.triggerTrendingRecalcForAllWires(),
    });

    this.refresh();
  }

  refresh() {
    const locationState = getLocationState();
    const page = (+locationState.p || this.state.page);

    this.loading.start();
    getPaginated(page)
      .then(result => this.setState({
        page,
        wires: result.data.wires,
        totalWireCount: result.data.total_wire_count,
        totalPageCount: result.data.total_page_count,
      }))
      .catch(e => defaultOnErrorHandler(e, 'Error loading trending feed'))
      .finally(() => this.loading.stop());
  }

  triggerTrendingRecalcForAllWires() {
    if (this.state.alreadyTriggeredTrendingRecalcForAllWires) {
      // eslint-disable-next-line no-alert
      alert("You've already triggered the recalc. Don't do it again! Be patient. Ceiling cat knows all.");
      return;
    }

    this.setState({ alreadyTriggeredTrendingRecalcForAllWires: true });

    const wiresPerMinute = 100;
    const estimatedTime = this.state.totalWireCount / wiresPerMinute / 60;
    const message = `There are ${this.state.totalWireCount} wires in the system, which will take approximately ${estimatedTime.toFixed(0)} hours to complete at ${wiresPerMinute} wires per minute. Are you sure you want to trigger a full recalc?`;

    // eslint-disable-next-line no-alert
    if (!window.confirm(message)) return;

    recalcAllWires()
      .then(() => toastr.success('Trending score recalculation has been triggered for all wires'))
      .catch(e => defaultOnErrorHandler(e, 'Error triggering trending score recalculation'));
  }

  retrieveWireStatistics(event, wireId) {
    event && event.preventDefault && event.preventDefault();
    getWireStats(wireId)
      .then(result => this.alert.present('Statistics for wire', <WireStatTable {...result.data} />))
      .catch(e => defaultOnErrorHandler(e, 'Error getting statistics for wire'));
  }

  render() {
    return (
      <ErrorBoundary>
        <div className="trending-table">
          <GlobalStyle />
          <Loading ref={c => (this.loading = c)}>
            <Pagination totalPageCount={this.state.totalPageCount} page={this.state.page} pageUrl="/lwadmin/trending" />
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Published at</th>
                  <th>
                    Time engagement score (
                    <Hint text="(number of wire impressions / number of feed impressions) - (2.5 * age of wire in days)" />
                    )
                  </th>
                  <th>
                    Engagement score (
                    <Hint text="0.2 * number of likes + 0.2 * number of comments + 0.6 * number of shares + 0.2 * number of profile link clicks + 0.3 * number of external link clicks" />
                    )
                  </th>
                  <th>
                    Trending score (
                    <Hint text="Weighted sum of time engagement score + engagement score" />
                    )
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {this.state.wires.map(wire => (
                  <tr key={wire.id}>
                    <th>{wire.id}</th>
                    <td>{wire.title}</td>
                    <td>{new Date(wire.published_at).toLocaleString()}</td>
                    <td>{(+(wire.time_engagement_score)).toFixed(2)}</td>
                    <td>{(+(wire.engagement_score)).toFixed(2)}</td>
                    <td>{(+(wire.trending_score)).toFixed(2)}</td>
                    <td className="table-tools">
                      <ul>
                        <li><a href={`/wires/${wire.slug}`}>View</a></li>
                        <li><a href={`/admin/wires/${wire.slug}/edit`}>Edit</a></li>
                        <li><a href="#" onClick={e => this.retrieveWireStatistics(e, wire.id)}>Stats</a></li>
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination totalPageCount={this.state.totalPageCount} page={this.state.page} pageUrl="/lwadmin/trending" />
          </Loading>
          <Alert ref={c => (this.alert = c)} />
        </div>
      </ErrorBoundary>
    );
  }
}
